// frontend/src/components/grants/GrantUsageBar.tsx
import { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { formatMoney, grantRemaining } from '@/lib/money';
import { useMsg } from '@/i18n/msg';
import { PendingDecision } from '@/components/editorial';
import type { GrantCurrency, GrantUsage } from '@/types/api';

/**
 * A thin applied/pending/remaining bar for a grant's budget. Amounts print
 * in the award's currency (2026-09-19: a Canadian award reads "CA$1,500").
 * The server adds `proof_cents` / `proof_count` to a SIGNATURE grant's usage
 * only (backend/src/services/grantUsage.js); their presence is how this bar
 * knows the grant is a Signature one (#2954).
 *
 * `showBudgetSummary` (default true) controls the two figures that name the
 * BUDGET rather than its composition: the remaining sentence on the right,
 * and the "of $X approved" caption under the track. Pass false where the
 * caller already states them on an adjacent line — today only
 * `GrantReceipts`, which sits directly beneath a `GrantRow` that prints the
 * same `grantRemaining()` sentence (#2097: the open card read "$11,400 left
 * of $12,000" twice, and named $12,000 three times). The standalone callers
 * — the dashboard's GrantsCard and the TRFer FamilyProfilePage — have no such
 * line above them and keep the default.
 *
 * #2954 slice A: on a Signature grant the receipts linked to it are proof of
 * use. They are not in the applied figure and do not change "remaining"; the
 * bar names them on their own line ("2 receipts show how $1,800 was used").
 */
export function GrantUsageBar({ usage, currency = 'USD', showBudgetSummary = true }: { usage: GrantUsage; currency?: GrantCurrency; showBudgetSummary?: boolean }) {
  const { t } = useTranslation('grants');
  const resolve = useMsg();
  const formatCents = (cents: number | null | undefined) => formatMoney(cents, currency);
  const remaining = grantRemaining(usage, currency);
  const approved = usage.approved_cents ?? 0;
  const pct = (n: number) => (approved > 0 ? Math.min(100, Math.round((n / approved) * 100)) : 0);
  const appliedPct = pct(usage.applied_cents);
  const pendingPct = Math.min(100 - appliedPct, pct(usage.pending_cents));
  const isSignatureProof = usage.proof_count != null;
  const proofCount = usage.proof_count ?? 0;

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between text-body-sm">
        {/* One catalog sentence per case (#2109 R3-B3): "$600 applied" or
            "$600 applied · $50 pending", the figures styled through slots. */}
        <span className="inline-flex items-center gap-1 text-stevens-ink-3">
          <span>
            <Trans
              t={t}
              i18nKey={usage.pending_cents > 0 ? 'usage.appliedPending' : 'usage.applied'}
              values={{ amount: formatCents(usage.applied_cents), pending: formatCents(usage.pending_cents) }}
              components={{
                amount: <span className="font-medium text-foreground tabular-nums" />,
                pending: <span className="tabular-nums" />,
              }}
            />
          </span>
          {/* The applied figure is what the decision changes: on Signature it
              leaves the linked receipts out. Staff-only, like every marker. */}
          {isSignatureProof && (
            <PendingDecision id="signature-receipts-prove-not-reimburse" side="bottom" />
          )}
        </span>
        {/* Wording from lib/money (2026-09-21) — the row prints the same
            sentence, and "Over budget" must not exist in two places. When a
            caller already prints it above the bar, it exists in ONE place by
            not being drawn here at all (#2097). */}
        {showBudgetSummary && (
          <span className={cn('tabular-nums', usage.over_applied ? 'text-status-danger-fg font-medium' : 'text-stevens-ink-3')}>
            {!remaining
              ? formatCents(usage.remaining_cents)
              : remaining.overBudget
                ? resolve(remaining.text)
                : (
                  // Styled as one phrase here, so the two halves are fragments.
                  <Trans
                    t={t}
                    i18nKey="remaining.leftOf"
                    values={{ amount: remaining.amount, approved: remaining.approved }}
                    components={{ amount: <Fragment />, rest: <Fragment /> }}
                  />
                )}
          </span>
        )}
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted flex">
        <div className="h-full bg-stevens-indigo" style={{ width: `${appliedPct}%` }} />
        <div className="h-full bg-stevens-indigo/30" style={{ width: `${pendingPct}%` }} />
      </div>
      {showBudgetSummary && usage.approved_cents != null && (
        <p className="text-label text-stevens-ink-3">{t('usage.ofApproved', { amount: formatCents(usage.approved_cents) })}</p>
      )}
      {isSignatureProof && proofCount > 0 && (
        <p className="text-label text-stevens-ink-3" data-testid="grant-usage-proof">
          {t('usage.proof', { count: proofCount, amount: formatCents(usage.proof_cents ?? 0) })}
        </p>
      )}
    </div>
  );
}

